import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { X, KeyRound, ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * PhaseSubmitPinModal — Phase-3 A2.
 *
 * Blocks Submit until the person hitting Submit types their 4-digit PIN.
 * The PIN is verified against the active employee master via
 * POST /shifts/verify_pin; on success we resolve to a { code, name } that
 * gets threaded back to the submit call. Plaintext is never persisted to
 * localStorage or logged; it lives in component state only.
 *
 * UX: on the shift-close phone, a numeric keypad is the fastest input —
 * hence the inputMode="numeric" and single-field 4-slot look.
 *
 * Props:
 *   open       — boolean
 *   onCancel   — () => void
 *   onVerified — ({ code, name, pin }) => void   // pin passed through so
 *                                                 // parent can POST it to the
 *                                                 // /shifts endpoint that
 *                                                 // does the re-verify.
 *   title      — optional heading override
 */
export default function PinSubmitModal({ open, onCancel, onVerified, title = "Confirm with your 4-digit PIN" }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resolved, setResolved] = useState(null);   // { code, name }
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin("");
      setError("");
      setResolved(null);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!/^\d{4}$/.test(pin)) {
      setError("PIN must be exactly 4 digits.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/shifts/verify_pin", { pin });
      setResolved(r.data);
      // Auto-hand off after a beat so the user sees the resolved name.
      setTimeout(() => onVerified({ ...r.data, pin }), 400);
    } catch (e) {
      setError(apiError(e) || "That PIN doesn't match any active employee.");
      setResolved(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="pin-modal-root"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-white border-2 border-slate-900 rounded-sm w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-300 bg-slate-50">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-slate-700" />
            <div className="font-bold text-slate-900 text-sm uppercase tracking-wider">{title}</div>
          </div>
          <button data-testid="pin-modal-close" onClick={onCancel} className="p-1 hover:bg-slate-200 rounded-sm">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-600">
            Every submit is stamped with the person hitting Submit. Type your
            own 4-digit PIN so this shift is attributed to you — not the
            person who filled the readings earlier. Ask the admin to reset
            it if you&apos;ve forgotten yours.
          </p>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Your PIN</label>
            <input
              ref={inputRef}
              data-testid="pin-modal-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              value={pin}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                setPin(digits);
                setError("");
                setResolved(null);
              }}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="mt-1 w-full h-14 border-2 border-slate-400 rounded-sm text-center text-2xl tracking-[0.5em] font-mono bg-slate-50 focus:bg-white focus:border-slate-900 outline-none"
              placeholder="••••"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-2 border-2 border-red-500 bg-red-50 rounded-sm text-sm text-red-800" data-testid="pin-modal-error">
              <ShieldAlert size={14} />
              <span>{error}</span>
            </div>
          )}

          {resolved && (
            <div className="flex items-center gap-2 p-2 border-2 border-emerald-500 bg-emerald-50 rounded-sm text-sm text-emerald-800" data-testid="pin-modal-resolved">
              <ShieldCheck size={14} />
              <span>Submitting as <b>{resolved.name}</b> · {resolved.category || "staff"}</span>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button data-testid="pin-modal-cancel" variant="outline" onClick={onCancel} className="border-slate-300" disabled={busy}>
              Cancel
            </Button>
            <Button data-testid="pin-modal-submit" onClick={submit} disabled={busy || pin.length !== 4} className="bg-slate-900 hover:bg-slate-800 text-white">
              {busy ? "Verifying…" : "Verify & submit"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
